import { PageProps } from '..'
import ErrorBoundary from '@components/ErrorBoundary'
import { type SizingWidgetData } from '@helpers/products/sizingWidgetData'
import styles from '../styles.module.scss'
import PageSection from '@components/ui/PageSection'
import HeroBanner from '@components/pages/components/HeroBanner'
import ShoppableCards from '@components/pages/components/ShoppableCardsSection'
import HeroVideoBanner from '@components/pages/components/HeroVideoBanner'
import HeroBannerAnimated from '@components/pages/components/HeroBannerAnimated'
import StatementSection from '@components/pages/components/StatementSection'
import RichTextSection from '@components/pages/components/RichTextSection'
import Benefits from '@components/ui/Benefits'
import ProductsBreakdown from '@components/ui/ProductsBreakdown'
import LandingPageProductsCards from '@components/pages/components/LandingPageProductsCards'
import Reviews from '@components/pages/components/Reviews'
import PageHeader from '@components/pages/components/PageHeader'
import FeatureCards from '@components/pages/components/FeatureCards'
import SectionHeadline from '@components/pages/components/SectionHeadline'
import IconCards from '@components/pages/components/IconCards'
import ImageCopySection from '@components/pages/components/ImageCopySection'
import SizingWidget from '@components/product/SizingWidget'
import ComparisonCards from '@components/pages/components/ComparisonCards'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'
import classNames from 'classnames'

const FULL_BLEED_COMPONENTS = [
  'heroBannerAnimated',
  'heroVideoBanner',
  'heroBanner',
  'imageCarousel',
  'imageCopySection',
  'shoppableCards',
  'featureCards'
]

const THIN_SPACING_MOBILE = [
  'heroBannerAnimated',
  'heroVideoBanner',
  'heroBanner',
  'imageCarousel',
  'shoppableCards'
]

const THIN_SPACING_DESKTOP = [
  'heroBannerAnimated',
  'heroVideoBanner',
  'heroBanner',
  'imageCarousel',
  'featureCards',
  'imageCopySection'
]

type MappedComponentProps = PageProps & {
  rootPath: boolean
  firstTimeUser: boolean
  pageType?: string
  sectionClasses: string
}

function MappedComponent({ type, ...props }: MappedComponentProps) {
  if (type === 'imageCarousel') {
    const pageData = props as React.ComponentProps<typeof HeroBanner>
    return <HeroBanner {...pageData} />
  }

  if (type === 'shoppableCards') {
    const pageData = props as React.ComponentProps<typeof ShoppableCards>
    return <ShoppableCards {...pageData} />
  }

  if (type === 'heroVideoBanner') {
    const pageData = props as React.ComponentProps<typeof HeroVideoBanner>
    return <HeroVideoBanner {...pageData} />
  }

  if (type === 'heroBannerAnimated') {
    const pageData = props as React.ComponentProps<typeof HeroBannerAnimated>
    return <HeroBannerAnimated {...pageData} />
  }

  if (type === 'statementSection') {
    const pageData = props as React.ComponentProps<typeof StatementSection>
    return <StatementSection {...pageData} />
  }

  if (type === 'richTextSection') {
    const pageData = props as React.ComponentProps<typeof RichTextSection>
    return <RichTextSection {...pageData} />
  }

  if (type === 'benefits') {
    const pageData = props as React.ComponentProps<typeof Benefits>
    return <Benefits {...pageData} />
  }

  if (type === 'productBreakdown') {
    const pageData = props as React.ComponentProps<typeof ProductsBreakdown>
    return <ProductsBreakdown {...pageData} />
  }

  if (type === 'landingPageProductCards') {
    const pageData = props as React.ComponentProps<
      typeof LandingPageProductsCards
    >
    return <LandingPageProductsCards {...pageData} />
  }

  if (type === 'reviews') {
    const pageData = props as React.ComponentProps<typeof Reviews>
    return <Reviews {...pageData} />
  }

  if (type === 'pageHeader') {
    const pageData = props as React.ComponentProps<typeof PageHeader>
    return <PageHeader {...pageData} />
  }

  if (type === 'featureCards') {
    const pageData = props as React.ComponentProps<typeof FeatureCards>

    return <FeatureCards {...pageData} />
  }

  if (type === 'pageHeadline') {
    const pageData = props as React.ComponentProps<typeof SectionHeadline>

    return <SectionHeadline {...pageData} />
  }

  if (type === 'iconCards') {
    const pageData = props as React.ComponentProps<typeof IconCards>

    return <IconCards {...pageData} />
  }

  if (type === 'imageCopySection') {
    const pageData = props as React.ComponentProps<typeof ImageCopySection>

    return <ImageCopySection {...pageData} />
  }

  if (type === 'sizingWidget') {
    const pageData = props as React.ComponentProps<typeof SizingWidget>

    return (
      <SizingWidget
        sizingWidgetData={{ ...(pageData as unknown as SizingWidgetData) }}
        sectionClasses={pageData.sectionClasses}
      />
    )
  }

  if (type === 'comparisonCards') {
    const pageData = props as React.ComponentProps<typeof ComparisonCards>

    return <ComparisonCards {...pageData} />
  }

  return null
}

function isImageCopySectionData(
  data: PageProps
): data is React.ComponentProps<typeof ImageCopySection> {
  return data?.type === 'imageCopySection'
}

function getFullBleedNext(nextPageData: PageProps) {
  if (
    isImageCopySectionData(nextPageData) &&
    nextPageData.copyBackgroundTransparent === false
  ) {
    return false
  }
  return FULL_BLEED_COMPONENTS.includes(nextPageData?.type || '')
}

type PageComponentProps = {
  index: number
  pageData: PageProps
  nextPageData: PageProps
  rootPath: boolean
  firstTimeUser: boolean
  pageType?: string
}

export const PageComponent = ({
  index,
  pageData,
  nextPageData,
  rootPath,
  firstTimeUser,
  pageType
}: PageComponentProps) => {
  const isDesktop = useMediaQuery({
    minWidth: BREAKPOINT_SIZES.DESKTOP,
    initialState: true
  })
  const currentType = pageData.type
  const currentTypeSafe = currentType || ''
  const nextTypeSafe = nextPageData?.type || ''
  const isThinSpacing =
    FULL_BLEED_COMPONENTS.includes(nextTypeSafe || '') &&
    FULL_BLEED_COMPONENTS.includes(currentTypeSafe || '')

  const isFullBleedNext = getFullBleedNext(nextPageData)

  // Thin spacing logic by viewport
  const applyThinMobile =
    !isDesktop &&
    THIN_SPACING_MOBILE.includes(currentTypeSafe) &&
    !isDesktop &&
    isFullBleedNext

  const applyThinDesktop =
    isDesktop &&
    THIN_SPACING_DESKTOP.includes(currentTypeSafe) &&
    isFullBleedNext

  const sectionClassNames = classNames(
    styles.sectionSpacing,
    applyThinMobile && styles.sectionSpacingThinMobile,
    applyThinDesktop && styles.sectionSpacingThinDesktop
  )

  return (
    <ErrorBoundary key={index.toString()}>
      <PageSection isThinSpacing={false} classes={sectionClassNames}>
        <MappedComponent
          {...pageData}
          rootPath={rootPath}
          firstTimeUser={firstTimeUser}
          pageType={pageType}
          sectionClasses={`${styles.sectionSpacing} ${
            isThinSpacing ? styles.sectionSpacingThin : ''
          }`}
        />
      </PageSection>
    </ErrorBoundary>
  )
}
